import { FC } from "react";
import { useTranslation } from "../../hooks/useTranslationHook/useTranslation";
import { CenteredCard } from "../../containers/card";
import { Box, Button, Typography } from "@kamstrup/kfl";
import { commonStyles } from "../../styles/commonStyles";
import { TranslationKeyType } from "../../hooks/useTranslationHook/types";

interface AcceptInvitationStepProps {
  onAccept: () => void;
  onReject: () => void;
  organizationName: string;
  relationType: string;
  invitationState: string;
}

export const AcceptInvitationStep: FC<AcceptInvitationStepProps> = ({
  onAccept,
  onReject,
  organizationName,
  relationType,
  invitationState
}) => {
  const { translateString } = useTranslation();

  const isPrimary = relationType.trim().toLowerCase() === "primary";
  const isActionDisabled = invitationState !== "Created";

  const titleKey: TranslationKeyType = (() => {
    switch (invitationState) {
      case "Created":
        return "new_invitation";
      case "Completed":
        return "invitation_already_accepted";
      case "Cancelled":
        return "invitation_cancelled";
      case "Expired":
        return "invitation_expired";
      default:
        return "invitation_not_found";
    }
  })();

  const descriptionKey: TranslationKeyType = (() => {
    switch (invitationState) {
      case "Created":
        return isPrimary
          ? "you_have_received_primary_invitation"
          : "you_have_received_external_invitation";
      case "Completed":
        return "invitation_already_accepted_description";
      case "Cancelled":
        return "invitation_cancelled_description";
      case "Expired":
        return "invitation_expired_description";
      default:
        return "invitation_not_found_description";
    }
  })();

  return (
    <CenteredCard title={translateString(titleKey)}>
      <Typography variant="body4" sx={commonStyles.introText}>
        {translateString(descriptionKey)}
      </Typography>
      <Typography variant="body4" sx={{ ...commonStyles.introText, mb: 1 }}>
        {translateString("invitation_to")}:
      </Typography>
      <Typography variant="body4" sx={{ fontWeight: 600, mb: 4 }}>
        {organizationName}
      </Typography>
      <Box sx={commonStyles.buttonContainer}>
        <Button
          color="primary"
          variant="contained"
          fullWidth
          sx={commonStyles.button}
          disabled={isActionDisabled}
          onClick={onAccept}
        >
          {translateString("accept_invitation")}
        </Button>
      </Box>
      <Box sx={commonStyles.buttonContainer}>
        <Button
          color="primary"
          variant="outlined"
          fullWidth
          sx={commonStyles.button}
          disabled={isActionDisabled}
          onClick={onReject}
        >
          {translateString("reject_invitation")}
        </Button>
      </Box>
    </CenteredCard>
  );
};
